/**
 * DocumentContent - Card de documento para download
 */
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { getFileIcon } from "../../../../utils/fileIcons";
import { downloadMedia } from "@/lib/mediaDownload";

export function DocumentContent({
  filename,
  size,
  mimeType,
  downloadUrl,
  caption,
  className,
}) {
  const { resolved: currentTheme } = useTheme();
  const isDark = currentTheme === "dark";
  const fileIcon = getFileIcon(filename, mimeType);

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = () => downloadMedia(downloadUrl, filename);

  return (
    <>
      <div
        className={cn(
          "flex items-center gap-3 p-3 rounded-lg border",
          isDark
            ? "bg-surface-active/50 border-border-strong"
            : "bg-white border-neutral-200",
          "max-w-[280px]",
          className,
        )}
      >
        <div
          className={cn(
            "w-10 h-10 shrink-0 rounded flex items-center justify-center p-1",
            isDark ? "bg-white/5" : "bg-neutral-50 border border-neutral-200",
          )}
        >
          <img
            src={fileIcon}
            alt="File"
            className="w-full h-full object-contain"
          />
        </div>

        <div className="flex-1 min-w-0">
          <p
            className={cn(
              "text-sm truncate font-medium",
              isDark ? "text-foreground" : "text-gray-800",
            )}
          >
            {filename || "Documento"}
          </p>
          {size && (
            <p
              className={cn(
                "text-xs",
                isDark ? "text-foreground-muted" : "text-gray-500",
              )}
            >
              {formatSize(size)}
            </p>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-8 w-8",
            isDark
              ? "text-foreground-muted hover:text-foreground"
              : "text-gray-500 hover:text-gray-800",
          )}
          onClick={handleDownload}
        >
          <Download className="h-4 w-4" />
        </Button>
      </div>
      {caption && (
        <p
          className={cn(
            "text-sm mt-1 whitespace-pre-wrap break-words max-w-[280px]",
            isDark ? "text-foreground" : "text-gray-800",
          )}
        >
          {caption}
        </p>
      )}
    </>
  );
}
